import React from 'react';
import { statesService, type CountryStatesGroup } from '../../../../services/statesService';

type LocationMetadataState = {
    countries: string[];
    statesByCountry: Record<string, string[]>;
};

let locationMetadataCache: LocationMetadataState | null = null;

export const useStoreDetailsLocationMetadata = (shouldLoad: boolean) => {
    const [metadata, setMetadata] = React.useState<LocationMetadataState>(
        locationMetadataCache ?? { countries: [], statesByCountry: {} },
    );
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (!shouldLoad || locationMetadataCache) {
            if (locationMetadataCache && metadata.countries.length === 0) {
                setMetadata(locationMetadataCache);
            }
            return;
        }

        let mounted = true;

        const loadLocationMetadata = async () => {
            try {
                setLoading(true);
                const statesResponse = await statesService.getStates();
                const groups: CountryStatesGroup[] = statesResponse?.data || [];
                const nextMetadata = {
                    countries: groups.map((group) => group.country_code),
                    statesByCountry: groups.reduce<Record<string, string[]>>((accumulator, group) => {
                        accumulator[group.country_code] = (group.states || []).map((state) => state.state_code);
                        return accumulator;
                    }, {}),
                };
                locationMetadataCache = nextMetadata;
                if (mounted) {
                    setMetadata(nextMetadata);
                }
            } catch (error) {
                console.error('Failed to load location metadata:', error);
                if (mounted) {
                    setMetadata({ countries: [], statesByCountry: {} });
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        void loadLocationMetadata();

        return () => {
            mounted = false;
        };
    }, [metadata.countries.length, shouldLoad]);

    return {
        countries: metadata.countries,
        statesByCountry: metadata.statesByCountry,
        locationMetadataLoading: loading,
    };
};
